// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

import React from "react";
import ExecutionSection from "../shared/ExecutionSection";
import ExecutionMetricTile from "../shared/ExecutionMetricTile";
import ExecutionTimeline from "../shared/ExecutionTimeline";
import ExecutionActionRow from "../shared/ExecutionActionRow";

function displayValue(value, fallback = "—") {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    const text = value.map((item) => displayValue(item, "")).filter(Boolean).join(", ");
    return text || fallback;
  }
  if (typeof value === "object") {
    const candidate = value.label || value.title || value.name || value.value || value.status || value.state || value.id;
    if (candidate !== undefined && candidate !== null && candidate !== value) return displayValue(candidate, fallback);
    try {
      return JSON.stringify(value);
    } catch (error) {
      return fallback;
    }
  }
  return String(value);
}

function clean(value) {
  const text = String(value ?? "").trim();
  return text || "";
}

function titleCase(value) {
  const text = clean(value);
  if (!text) return "—";
  return text.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatPercentish(value) {
  if (value === null || value === undefined || value === "") return "—";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return String(value);
  return `${Math.round(parsed)}%`;
}

function latestValidationValue(task, validationSummary, latestLink, key, fallback = "") {
  return (
    clean(task?.[key]) ||
    clean(validationSummary?.[key]) ||
    clean(validationSummary?.latest?.[key]) ||
    clean(latestLink?.[key]) ||
    fallback
  );
}

function normalizeRerunResult(task, validationSummary) {
  const direct = task?.validation_rerun_result;
  if (direct && typeof direct === "object") return direct;

  const fromSummary = validationSummary?.last_rerun;
  if (fromSummary && typeof fromSummary === "object") return fromSummary;

  const metadataRerun = task?.task_metadata?.last_validation_rerun || task?.raw_task?.task_metadata?.last_validation_rerun;
  if (metadataRerun && typeof metadataRerun === "object") return metadataRerun;

  return null;
}

function resultTone(result, closureAllowed = false) {
  const text = clean(result).toLowerCase();
  if (closureAllowed || ["pass", "passed", "success", "validated"].includes(text)) return "ok";
  if (text.includes("fail") || text.includes("block") || text.includes("error")) return "danger";
  return "default";
}

function resultColor(result, closureAllowed = false) {
  const tone = resultTone(result, closureAllowed);
  if (tone === "ok") return "#34d399";
  if (tone === "danger") return "#f87171";
  return "#60a5fa";
}

function firstArray(...values) {
  for (const value of values) {
    if (Array.isArray(value) && value.length) return value;
  }
  return [];
}

export default function ValidationExecutionTab({
  task = {},
  validationEvents = [],
  onRunValidation,
  validationRunning = false,
  onOpenTab,
}) {
  const validationSummary = task?.validation_summary && typeof task.validation_summary === "object" ? task.validation_summary : {};
  const latestLink = validationSummary?.latest?.link && typeof validationSummary.latest.link === "object" ? validationSummary.latest.link : {};
  const rerunResult = normalizeRerunResult(task, validationSummary);
  const rerunClosureGuard =
    rerunResult?.closure_guard && typeof rerunResult.closure_guard === "object" ? rerunResult.closure_guard : null;
  const closureGuard =
    rerunClosureGuard ||
    (validationSummary?.closure_guard && typeof validationSummary.closure_guard === "object" ? validationSummary.closure_guard : {});

  const latestResult =
    clean(rerunResult?.result) ||
    clean(rerunResult?.status) ||
    latestValidationValue(task, validationSummary, latestLink, "validation_status") ||
    latestValidationValue(task, validationSummary, latestLink, "result") ||
    latestValidationValue(task, validationSummary, latestLink, "status", "Not run");

  const confidence =
    rerunResult?.confidence_score ||
    task.validation_confidence ||
    task.confidence_score ||
    validationSummary.confidence_score ||
    latestLink.confidence_score ||
    validationSummary.confidence ||
    "—";

  const confidenceLevel = clean(rerunResult?.confidence_level || validationSummary.confidence_level);
  const closureAllowed =
    closureGuard.closure_allowed === true ||
    validationSummary.closure_allowed === true ||
    latestLink.closure_allowed === true;

  const guardStatus =
    clean(task.closure_guard_status) ||
    clean(closureGuard.status) ||
    clean(closureGuard.state) ||
    (closureAllowed ? "Closure allowed" : "Closure blocked");

  const guardReason =
    clean(rerunResult?.reason) ||
    clean(task.closure_guard_reason) ||
    clean(task.validation_blocker) ||
    clean(closureGuard.reason) ||
    clean(closureGuard.blocker_reason) ||
    clean(validationSummary.blocker_reason) ||
    clean(validationSummary.reason) ||
    clean(latestLink.blocker_reason) ||
    clean(latestLink.reason) ||
    "No closure guard reason was provided.";

  const evidenceState =
    clean(task.evidence_state) ||
    clean(task.evidence_summary?.status) ||
    clean(validationSummary.evidence_status) ||
    clean(latestLink.evidence_status) ||
    (Array.isArray(rerunResult?.evidence_link_ids) && rerunResult.evidence_link_ids.length ? "Used" : "") ||
    "Task scoped";

  const validationResultIds = firstArray(rerunResult?.validation_result_ids);
  const evidenceLinkIds = firstArray(rerunResult?.evidence_link_ids);
  const resultText = clean(latestResult).toLowerCase();
  const blocked =
    !closureAllowed &&
    (resultText.includes("fail") ||
      resultText.includes("block") ||
      clean(guardStatus).toLowerCase().includes("block") ||
      clean(guardReason).toLowerCase().includes("fail"));

  const rerunAvailable = Boolean(rerunResult);
  const resultBadgeTone = resultTone(latestResult, closureAllowed);

  return (
    <>
      <ExecutionSection
        title="Validation State"
        description="Task-scoped validation result, closure guard, and deterministic rerun controls."
        badge={displayValue(latestResult, "Not run")}
        badgeTone={resultBadgeTone}
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 14 }}>
          <ExecutionMetricTile label="Latest Result" value={titleCase(latestResult)} color={resultColor(latestResult, closureAllowed)} />
          <ExecutionMetricTile
            label="Confidence"
            value={formatPercentish(confidence)}
            sub={confidenceLevel ? titleCase(confidenceLevel) : ""}
            color="#34d399"
          />
          <ExecutionMetricTile label="Closure Guard" value={guardStatus} color={blocked ? "#f87171" : "#60a5fa"} />
          <ExecutionMetricTile label="Evidence" value={evidenceState} />
        </div>

        <p style={{ color: "#bfdbfe", fontSize: 13, lineHeight: 1.55, margin: 0 }}>
          <strong style={{ color: "#f8fafc" }}>Guard reason:</strong> {displayValue(guardReason)}
        </p>

        <p style={{ color: "#93c5fd", fontSize: 12.5, lineHeight: 1.5, margin: 0 }}>
          Deterministic validation re-runs use the latest source-state already loaded into OASYS. The modal stays open and refreshes the task context after the run.
        </p>

        <ExecutionActionRow
          actions={[
            {
              key: "run-validation",
              label: validationRunning ? "Running Validation…" : "Run Validation",
              onClick: onRunValidation,
              disabled: validationRunning,
              variant: "primary",
            },
            { key: "evidence", label: "View Evidence", onClick: () => onOpenTab?.("evidence") },
            { key: "history", label: "View History", onClick: () => onOpenTab?.("history") },
          ]}
        />
      </ExecutionSection>

      {rerunAvailable ? (
        <ExecutionSection
          title="Latest Rerun Result"
          description="Definitive result returned by the Week 34 deterministic validation rerun endpoint."
          badge={titleCase(rerunResult?.result || rerunResult?.status)}
          badgeTone={resultBadgeTone}
        >
          <div
            style={{
              border: `1px solid ${blocked ? "rgba(248, 113, 113, 0.42)" : "rgba(52, 211, 153, 0.34)"}`,
              borderRadius: 18,
              padding: 16,
              background: blocked ? "rgba(127, 29, 29, 0.16)" : "rgba(16, 185, 129, 0.10)",
              display: "grid",
              gap: 12,
            }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 14 }}>
              <ExecutionMetricTile label="Result" value={titleCase(rerunResult?.result || rerunResult?.status)} color={resultColor(latestResult, closureAllowed)} />
              <ExecutionMetricTile
                label="Confidence"
                value={formatPercentish(rerunResult?.confidence_score || confidence)}
                sub={confidenceLevel ? titleCase(confidenceLevel) : ""}
                color="#34d399"
              />
              <ExecutionMetricTile label="Closure" value={closureAllowed ? "Allowed" : "Blocked"} color={closureAllowed ? "#34d399" : "#f87171"} />
            </div>

            <p style={{ color: "#bfdbfe", fontSize: 13, lineHeight: 1.55, margin: 0 }}>
              <strong style={{ color: "#f8fafc" }}>Reason:</strong> {displayValue(rerunResult?.reason || guardReason)}
            </p>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: "#93c5fd", fontSize: 11, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                  Validation Run
                </div>
                <div style={{ color: "#f8fafc", fontSize: 13, lineHeight: 1.45, marginTop: 5, overflowWrap: "anywhere" }}>
                  {displayValue(rerunResult?.validation_run_id, "No run id returned.")}
                </div>
              </div>

              <div style={{ minWidth: 0 }}>
                <div style={{ color: "#93c5fd", fontSize: 11, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                  Validation Result IDs
                </div>
                <div style={{ color: "#f8fafc", fontSize: 13, lineHeight: 1.45, marginTop: 5, overflowWrap: "anywhere" }}>
                  {validationResultIds.length ? validationResultIds.join(", ") : "No validation result ids returned."}
                </div>
              </div>
            </div>

            <p style={{ color: "#93c5fd", fontSize: 12.5, lineHeight: 1.5, margin: 0 }}>
              Evidence links returned: {evidenceLinkIds.length}. Remediation link: {displayValue(rerunResult?.remediation_validation_link_id, "not returned")}.
            </p>
          </div>
        </ExecutionSection>
      ) : null}

      <ExecutionSection
        title="Validation History"
        description="All task-scoped validation runs from Mission Execution and the Validation dashboard, newest first."
      >
        <ExecutionTimeline events={validationEvents} />
      </ExecutionSection>
    </>
  );
}
